// useRuns — fetches /runs every POLL_INTERVAL_MS and exposes the run list as
// view-models. Returns boot status so the App can render a "platform not
// running" empty-state when /runs returns 503 (DATABASE_URL missing).
//
// We poll rather than subscribe at the list level: there is currently no
// platform-wide live event channel — only per-run streams. Polling /runs
// every 5s is light: typical responses are < 50 KB and the server already
// hits a single Postgres query.

import { useEffect, useRef, useState, useCallback } from 'react';
import * as api from '../lib/api';
import { ApiError, classifyFetchError, type FetchErrorKind } from '../lib/api';
import { captureError } from '../lib/sentry';
import type { ApiRun, RunVM } from '../lib/types';
import { runToVM } from '../lib/runMapping';
import { MOCK_RUNS } from '../lib/mocks';

const POLL_INTERVAL_MS = 5000;

export type BootStatus =
  | { kind: 'loading' }
  | { kind: 'ok' }
  | { kind: 'no-persistence' }
  // `category` drives the UI guidance (auth link vs. server down vs. raw).
  // `message` is the raw failure text — used as a fallback / debug detail.
  // `status` and `bodyExcerpt` are populated when the API returned a non-OK
  // response so the UI can show "API returned 502" with body context.
  | {
      kind: 'unreachable';
      category: FetchErrorKind;
      message: string;
      status?: number;
      bodyExcerpt?: string;
    };

interface UseRunsResult {
  runs: RunVM[];
  apiRuns: ApiRun[];
  status: BootStatus;
  refresh: () => Promise<void>;
}

const USE_MOCKS = (import.meta.env?.VITE_USE_MOCKS as string | undefined) === 'true';

export function useRuns(): UseRunsResult {
  const [apiRuns, setApiRuns] = useState<ApiRun[]>([]);
  const [status, setStatus] = useState<BootStatus>({ kind: 'loading' });
  const mounted = useRef(true);

  const fetchOnce = useCallback(async () => {
    if (USE_MOCKS) {
      setApiRuns(MOCK_RUNS);
      setStatus({ kind: 'ok' });
      return;
    }
    try {
      const res = await api.listRuns({ limit: 200 });
      if (!mounted.current) return;
      setApiRuns(res.runs);
      setStatus({ kind: 'ok' });
    } catch (err) {
      if (!mounted.current) return;
      // 503 from /runs has a specific meaning: the server is up but
      // DATABASE_URL is missing. Keep the dedicated empty-state.
      if (err instanceof ApiError && err.status === 503) {
        setStatus({ kind: 'no-persistence' });
        return;
      }
      const category = classifyFetchError(err);
      const message = err instanceof Error ? err.message : String(err);
      const next: Extract<BootStatus, { kind: 'unreachable' }> = {
        kind: 'unreachable',
        category,
        message,
      };
      if (err instanceof ApiError) {
        next.status = err.status;
        const body = err.body;
        if (body !== undefined && body !== null) {
          const excerpt = typeof body === 'string'
            ? body
            : JSON.stringify(body);
          next.bodyExcerpt = excerpt.slice(0, 200);
        }
      }
      setStatus(next);
      // Report to Sentry with the classified kind as a tag so the operator
      // can filter "show me all the cors-or-auth failures from the past
      // hour" cleanly. cors-or-auth and aborted are excluded because:
      // - cors-or-auth fires while the user is signing in; not an error.
      // - aborted fires when the component unmounts or a refresh races
      //   another fetch; expected, not actionable.
      if (category !== 'cors-or-auth' && category !== 'aborted') {
        captureError(err, {
          tags: { area: 'fetch', endpoint: 'listRuns', kind: category },
          extras: {
            status: err instanceof ApiError ? err.status : undefined,
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void fetchOnce();
    const id = window.setInterval(() => { void fetchOnce(); }, POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
    };
  }, [fetchOnce]);

  const runs = apiRuns.map(runToVM);
  return { runs, apiRuns, status, refresh: fetchOnce };
}
